/**
 * Iter 51.7 — Proposal Module Codes admin (2026-05-19)
 *
 * Operator-managed master list of `module_code` values that appear in
 * proposal numbers (`REE/{module}/####/{FY}`). Replaces the hardcoded
 * `MODULE_CODES` tuple previously held in `Proposals.jsx`.
 *
 * Governance (locked per operator directive):
 *   - Primary super-admin only (rahul@reedos.com) for any mutation;
 *     other super_admins can READ via the dropdown but not change the list.
 *   - `code` is **immutable once created** — only `label` / `display_order`
 *     are editable. Preserves proposal-number traceability.
 *   - Soft-disable only. Codes that have been referenced historically
 *     remain in the DB; they're just hidden from the new-proposal
 *     dropdown.
 *   - Audit log entries on every mutation (backend).
 */
import { useEffect, useMemo, useState } from "react";
import {
    Stack, Plus, PencilSimple, Power, Warning, ArrowsClockwise, CheckCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";


export default function ProposalModuleCodes() {
    const { user } = useAuth();
    const isPrimaryAdmin = !!user?.is_primary_admin;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [includeInactive, setIncludeInactive] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);  // module row OR null
    const [confirmDisable, setConfirmDisable] = useState(null);  // module row OR null
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/billing/proposal-modules/list?include_inactive=${includeInactive}`);
            setRows(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load module codes"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeInactive]);

    const activeCount = useMemo(() => rows.filter((r) => r.is_active).length, [rows]);
    const disabledCount = useMemo(() => rows.filter((r) => !r.is_active).length, [rows]);

    const handleCreate = async (form) => {
        setBusy(true);
        try {
            await api.post("/billing/proposal-modules", form);
            toast.success(`Module code '${form.code}' created.`);
            setCreateOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Create failed"));
        } finally {
            setBusy(false);
        }
    };

    const handlePatch = async (target, updates) => {
        setBusy(true);
        try {
            await api.patch(`/billing/proposal-modules/${target.id}`, updates);
            toast.success(`Updated '${target.code}'.`);
            setEditTarget(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        } finally {
            setBusy(false);
        }
    };

    const handleDisable = async (target) => {
        setBusy(true);
        try {
            await api.post(`/billing/proposal-modules/${target.id}/disable`);
            toast.success(`'${target.code}' disabled. Existing proposal numbers remain valid.`);
            setConfirmDisable(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Disable failed"));
        } finally {
            setBusy(false);
        }
    };

    const handleEnable = async (target) => {
        setBusy(true);
        try {
            await api.post(`/billing/proposal-modules/${target.id}/enable`);
            toast.success(`'${target.code}' re-enabled.`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Enable failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="proposal-module-codes-page">
            <PageHeader
                eyebrow="Admin · Proposal numbering"
                title={
                    <span className="flex items-center gap-3">
                        <Stack size={22} weight="duotone" className="text-indigo-600" />
                        Proposal module codes
                    </span>
                }
                description="Operator-managed master list embedded in proposal numbers (REE/{module}/####/{FY}). Codes are immutable once created — only labels and ordering are editable. Soft-disable hides a code from the new-proposal dropdown while keeping historical numbers valid."
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="modules-refresh">
                            <ArrowsClockwise size={13} /> Refresh
                        </Button>
                        {isPrimaryAdmin && (
                            <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5" data-testid="modules-add-btn">
                                <Plus size={13} /> Add code
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-5">
                {/* ── Filters + governance banner ── */}
                <div className="reedos-card p-3 flex items-center justify-between flex-wrap gap-3">
                    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={includeInactive}
                            onChange={(e) => setIncludeInactive(e.target.checked)}
                            className="w-3.5 h-3.5"
                            data-testid="modules-include-inactive"
                        />
                        Show disabled codes
                    </label>
                    <div className="text-[11px] text-slate-500">
                        <strong className="text-emerald-700">{activeCount}</strong> active ·{" "}
                        <strong className="text-slate-500">{disabledCount}</strong> disabled
                        {!isPrimaryAdmin && (
                            <span className="ml-3 text-amber-700 italic">
                                Read-only — only the primary super-admin can edit this list.
                            </span>
                        )}
                    </div>
                </div>

                {/* ── Table ── */}
                <div className="reedos-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50/60">
                            <tr className="text-left">
                                <th className="px-4 py-2.5 reedos-eyebrow text-[10px]">Order</th>
                                <th className="px-4 py-2.5 reedos-eyebrow text-[10px]">Code</th>
                                <th className="px-4 py-2.5 reedos-eyebrow text-[10px]">Label</th>
                                <th className="px-4 py-2.5 reedos-eyebrow text-[10px] text-right">Usage</th>
                                <th className="px-4 py-2.5 reedos-eyebrow text-[10px]">Status</th>
                                <th className="px-4 py-2.5 reedos-eyebrow text-[10px]">Last modified</th>
                                {isPrimaryAdmin && <th className="px-4 py-2.5 reedos-eyebrow text-[10px] text-right">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={isPrimaryAdmin ? 7 : 6} className="px-4 py-8 text-center text-xs text-slate-400">Loading…</td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={isPrimaryAdmin ? 7 : 6} className="px-4 py-8">
                                    <EmptyState
                                        icon={<Stack size={36} weight="thin" />}
                                        title="No module codes"
                                        description="Add the first code to start numbering proposals."
                                    />
                                </td></tr>
                            )}
                            {rows.map((r) => (
                                <tr
                                    key={r.id}
                                    className={classJoin(
                                        "border-t border-slate-100",
                                        !r.is_active && "bg-slate-50/40 opacity-75",
                                    )}
                                    data-testid={`module-row-${r.code}`}
                                >
                                    <td className="px-4 py-2 text-xs font-mono text-slate-500">{r.display_order}</td>
                                    <td className="px-4 py-2 text-xs font-mono font-bold text-slate-900">{r.code}</td>
                                    <td className="px-4 py-2 text-xs text-slate-700">{r.label}</td>
                                    <td className="px-4 py-2 text-xs text-right reedos-data text-slate-600">
                                        {r.proposal_count > 0 ? (
                                            <span title={`${r.proposal_count} proposals reference this code`}>
                                                {r.proposal_count} <span className="text-[9px] text-slate-400">proposal{r.proposal_count === 1 ? "" : "s"}</span>
                                            </span>
                                        ) : (
                                            <span className="text-slate-300">unused</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-xs">
                                        {r.is_active ? (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                                                <CheckCircle size={10} weight="fill" /> Active
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-600">
                                                Disabled
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-[10px] text-slate-500 font-mono">
                                        {(r.modified_at || "").slice(0, 10)}
                                        <span className="block text-[9px] text-slate-400">{r.modified_by}</span>
                                    </td>
                                    {isPrimaryAdmin && (
                                        <td className="px-4 py-2 text-right space-x-1">
                                            <Button size="sm" variant="ghost" onClick={() => setEditTarget(r)} className="h-7 px-2 text-[11px]" data-testid={`module-edit-${r.code}`}>
                                                <PencilSimple size={11} /> Edit
                                            </Button>
                                            {r.is_active ? (
                                                <Button size="sm" variant="ghost" onClick={() => setConfirmDisable(r)} className="h-7 px-2 text-[11px] text-rose-700 hover:text-rose-900" data-testid={`module-disable-${r.code}`}>
                                                    <Power size={11} /> Disable
                                                </Button>
                                            ) : (
                                                <Button size="sm" variant="ghost" onClick={() => handleEnable(r)} disabled={busy} className="h-7 px-2 text-[11px] text-emerald-700 hover:text-emerald-900" data-testid={`module-enable-${r.code}`}>
                                                    <Power size={11} /> Enable
                                                </Button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ── Methodology footer ── */}
                <div className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
                    <strong className="text-slate-700">Governance:</strong>{" "}
                    <span className="font-mono">code</span> is immutable once created — only the human-readable{" "}
                    <span className="font-mono">label</span> and dropdown <span className="font-mono">display_order</span>{" "}
                    can be edited. <strong className="text-slate-700">Disable, not delete</strong>{" "}
                    — codes that any proposal has used remain in the DB so historical numbers
                    (e.g. <span className="font-mono">REE/MFBO/0042/2026-27</span>) stay valid;
                    they're just hidden from the new-proposal dropdown. Every mutation is
                    recorded in the audit log with the acting user and timestamp.
                </div>
            </div>

            {/* ── Create dialog ── */}
            <CreateModuleDialog
                open={createOpen}
                busy={busy}
                onClose={() => setCreateOpen(false)}
                onSubmit={handleCreate}
                nextOrder={Math.max(0, ...rows.map((r) => r.display_order || 0)) + 10}
            />

            {/* ── Edit dialog ── */}
            <EditModuleDialog
                target={editTarget}
                busy={busy}
                onClose={() => setEditTarget(null)}
                onSubmit={handlePatch}
            />

            {/* ── Disable confirm ── */}
            <Dialog open={!!confirmDisable} onOpenChange={(v) => !v && setConfirmDisable(null)}>
                <DialogContent className="max-w-md" data-testid="module-disable-confirm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <Warning size={16} weight="duotone" className="text-rose-600" />
                            Disable module code?
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm text-slate-700">
                        <p>
                            <span className="font-mono font-bold text-slate-900">{confirmDisable?.code}</span>{" "}
                            ({confirmDisable?.label}) will be hidden from the new-proposal dropdown.
                        </p>
                        {confirmDisable?.proposal_count > 0 && (
                            <p className="bg-amber-50 border border-amber-200 rounded p-3 text-[11px] leading-relaxed">
                                <strong className="text-amber-800">{confirmDisable.proposal_count} historical proposal{confirmDisable.proposal_count === 1 ? "" : "s"} use{confirmDisable.proposal_count === 1 ? "s" : ""} this code.</strong>{" "}
                                Their proposal numbers remain valid — disabling only hides this code from new selections. You can re-enable it any time.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" size="sm" onClick={() => setConfirmDisable(null)} disabled={busy} data-testid="disable-cancel">Cancel</Button>
                        <Button size="sm" onClick={() => handleDisable(confirmDisable)} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5" data-testid="disable-confirm">
                            <Power size={11} /> Disable
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function CreateModuleDialog({ open, busy, nextOrder, onClose, onSubmit }) {
    const [code, setCode] = useState("");
    const [label, setLabel] = useState("");
    const [order, setOrder] = useState(nextOrder);
    useEffect(() => {
        if (open) {
            setCode("");
            setLabel("");
            setOrder(nextOrder);
        }
    }, [open, nextOrder]);
    const valid = code.trim().length > 0 && label.trim().length > 0;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="module-create-dialog">
                <DialogHeader>
                    <DialogTitle>Add proposal module code</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="reedos-eyebrow text-[10px]">Code · embedded in proposal numbers</Label>
                        <Input
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder="e.g. Advisory"
                            className="mt-1 font-mono"
                            data-testid="create-code-input"
                            maxLength={32}
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                            Becomes part of <span className="font-mono">REE/{code || "<code>"}/####/FY</span>. Immutable once created.
                        </p>
                    </div>
                    <div>
                        <Label className="reedos-eyebrow text-[10px]">Label · shown in the dropdown</Label>
                        <Input
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="e.g. Advisory Services"
                            className="mt-1"
                            data-testid="create-label-input"
                            maxLength={120}
                        />
                        <p className="text-[10px] text-slate-500 mt-1">
                            Operator can rename this any time without touching the code itself.
                        </p>
                    </div>
                    <div>
                        <Label className="reedos-eyebrow text-[10px]">Display order</Label>
                        <Input
                            type="number"
                            min={0}
                            max={9999}
                            value={order}
                            onChange={(e) => setOrder(parseInt(e.target.value || "0", 10))}
                            className="mt-1 font-mono w-32"
                            data-testid="create-order-input"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">Lower numbers appear earlier in the dropdown.</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="create-cancel">Cancel</Button>
                    <Button size="sm" onClick={() => onSubmit({ code: code.trim(), label: label.trim(), display_order: order })} disabled={busy || !valid} className="gap-1.5" data-testid="create-submit">
                        <Plus size={11} /> Add code
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function EditModuleDialog({ target, busy, onClose, onSubmit }) {
    const [label, setLabel] = useState("");
    const [order, setOrder] = useState(0);
    useEffect(() => {
        if (target) {
            setLabel(target.label || "");
            setOrder(target.display_order || 0);
        }
    }, [target]);
    if (!target) return null;
    const dirty = label.trim() !== target.label || order !== target.display_order;
    return (
        <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="module-edit-dialog">
                <DialogHeader>
                    <DialogTitle>Edit module code</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="reedos-eyebrow text-[10px]">Code (immutable)</Label>
                        <Input value={target.code} disabled className="mt-1 font-mono bg-slate-100" data-testid="edit-code-readonly" />
                        <p className="text-[10px] text-slate-500 mt-1">
                            Codes cannot be renamed once created — would break traceability of existing proposal numbers.
                        </p>
                    </div>
                    <div>
                        <Label className="reedos-eyebrow text-[10px]">Label</Label>
                        <Input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1" maxLength={120} data-testid="edit-label-input" />
                    </div>
                    <div>
                        <Label className="reedos-eyebrow text-[10px]">Display order</Label>
                        <Input type="number" min={0} max={9999} value={order} onChange={(e) => setOrder(parseInt(e.target.value || "0", 10))} className="mt-1 font-mono w-32" data-testid="edit-order-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={onClose} disabled={busy} data-testid="edit-cancel">Cancel</Button>
                    <Button size="sm" onClick={() => onSubmit(target, { label: label.trim(), display_order: order })} disabled={busy || !dirty} className="gap-1.5" data-testid="edit-submit">
                        Save changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
